import React, { useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import analytics from "@love-irish/analytics"
import {
  Command,
  CommandInput,
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "./base_ui"
import { useGlobalSearch } from "./use_global_search"
import { SearchDropdown } from "./search_dropdown"
import { TRENDING_SEARCHES } from "./trending"
import { searchPagePath } from "./search_page_path"
import type { SearchHit } from "./search_hit"

// Full-page navigation by default: results link to Rails pages (codex, collections, profiles, topics)
// that aren't react-router routes, so client-side navigation would render nothing. Injectable for tests.
const fullPageNavigate = (url: string): void => {
  window.location.assign(url)
}

export interface GlobalSearchBarProps {
  placeholder?: string
  className?: string
  // Injectable navigation seam (tests pass a spy); defaults to a full page load.
  onNavigate?: (url: string) => void
}

// A header typeahead over the multi-model `search` query, built on the base-UI combobox (cmdk Command
// inside a Radix Popover). This gives correct focus/keyboard behavior out of the box — the input keeps
// focus while arrow keys move the active item (aria-activedescendant), Enter selects it, and Tab /
// click-outside / Escape close the panel — instead of the fragile blur-timeout a hand-rolled dropdown
// needs. Debounce + min-length live in the hook, so it won't spam the API. Every search + click emits an
// analytics event so we can measure what people look for (and what's missing).
export const GlobalSearchBar: React.FC<GlobalSearchBarProps> = ({
  placeholder = "Cuardaigh (search)…",
  className,
  onNavigate = fullPageNavigate,
}) => {
  const [input, setInput] = useState("")
  const [open, setOpen] = useState(false)
  // The input lives in the PopoverAnchor (outside the content). We keep focus on it while the panel is
  // open, so Radix must NOT treat focus/clicks on the input as an "outside" dismissal (that would flash
  // the panel open then close it). This ref lets us exempt the anchor in onInteractOutside.
  const anchorRef = useRef<HTMLDivElement>(null)

  const { query, loading, groups, total } = useGlobalSearch(input)

  // "See all / view all": with a query, go to the results page (and log it); on the empty/trending state,
  // just open the search landing.
  const seeAll = (): void => {
    const q = input.trim()
    if (q) {
      analytics.track("search.submitted", {
        query: q,
        result_count: total,
        source: "header",
      })
      onNavigate(searchPagePath(q))
    } else {
      onNavigate("/search")
    }
    setOpen(false)
  }

  // Trending terms are links to /search?q=term (so hovering previews the URL). A plain click runs a live
  // search in place (fills the input; the hook debounces + fetches); modifier / middle clicks fall through
  // to the anchor's href and open the results page in a new tab.
  const selectTrending = (
    event: React.MouseEvent | undefined,
    term: string
  ): void => {
    if (
      event &&
      (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1)
    ) {
      setOpen(false)
      return
    }
    event?.preventDefault()
    setInput(term)
    setOpen(true)
  }

  const selectHit = (
    event: React.MouseEvent | undefined,
    { hit, position }: { hit: SearchHit; position: number }
  ): void => {
    analytics.track("search.result.clicked", {
      query,
      type: hit.__typename,
      path: hit.path,
      position,
    })
    // Let the browser handle modifier/middle clicks — the anchor's href opens them in a new tab.
    if (
      event &&
      (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1)
    ) {
      setOpen(false)
      return
    }
    event?.preventDefault()
    onNavigate(hit.path)
    setOpen(false)
  }

  return (
    // shouldFilter=false: results are already filtered server-side, so cmdk should show them as-is.
    <Command
      shouldFilter={false}
      className={twMerge(
        "w-full max-w-xs bg-transparent border-none rounded-none",
        className
      )}
    >
      {/* Opens on focus (showing the trending state before any query) and stays open while typing. */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div
            ref={anchorRef}
            className="flex items-center rounded border border-secondary-300 px-2 focus-within:border-dark"
          >
            <CommandInput
              value={input}
              onValueChange={(value: string) => {
                setInput(value)
                setOpen(true)
              }}
              onFocus={() => setOpen(true)}
              placeholder={placeholder}
              className="border-none outline-none"
              wrapperClass="border-none"
            />
          </div>
        </PopoverAnchor>

        <PopoverContent
          align="start"
          // Keep the caret in the input; cmdk drives item highlighting via aria-activedescendant.
          onOpenAutoFocus={(event: Event) => event.preventDefault()}
          // Don't yank focus back to the input when closing (avoids a focus→reopen loop).
          onCloseAutoFocus={(event: Event) => event.preventDefault()}
          // The input anchors the panel and keeps focus, so focus/clicks on it are not "outside" — exempt
          // it, otherwise Radix dismisses the panel the instant it opens. Real outside interactions still
          // close. Radix puts the actual node on detail.originalEvent.target (event.target is the layer).
          onInteractOutside={(event: {
            preventDefault: () => void
            detail?: { originalEvent?: { target?: EventTarget | null } }
          }) => {
            const node = event.detail?.originalEvent?.target as Node | null
            if (node && anchorRef.current?.contains(node)) {
              event.preventDefault()
            }
          }}
          className="w-md p-0"
        >
          <SearchDropdown
            query={query}
            loading={loading}
            total={total}
            groups={groups}
            trending={TRENDING_SEARCHES}
            onSelect={selectHit}
            onTrendingSelect={selectTrending}
            onSeeAll={seeAll}
          />
        </PopoverContent>
      </Popover>
    </Command>
  )
}

export default GlobalSearchBar
